import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import { 
  VerifiedUser as VerifiedIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { generateDnsRecords } from '../../config/dns';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface VerificationRecord {
  type: string;
  host: string;
  value: string;
  purpose: string;
  verified?: boolean;
}

const DomainVerification: React.FC = () => {
  const navigate = useNavigate();
  // const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [records, setRecords] = useState<VerificationRecord[]>([]);
  const [verificationStatus, setVerificationStatus] = useState<Record<string, boolean>>({});

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const domain = signupData.domain || 'example.com';

  useEffect(() => {
    if (!signupData.domain) {
      navigate('/setup/domain');
    }
    generateVerificationRecords();
  }, [signupData, navigate]);

  const generateVerificationRecords = async () => {
    try {
      // Fetch DNS records from backend (includes per-domain DKIM key)
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      if (currentData.domainId && currentData.token) {
        const response = await axios.get(`${API_URL}/signup/dns-records/${currentData.domainId}?token=${currentData.token}`);
        if (response.data?.success && response.data?.data?.records) {
          const apiRecords = response.data.data.records;
          const records: VerificationRecord[] = apiRecords.map((r: any) => ({
            type: r.type,
            host: r.host,
            value: r.value,
            purpose: r.description || `${r.type} record`,
          }));
          setRecords(records);
          return;
        }
      }
    } catch (err) {
      console.error('Error fetching DNS records, using fallback:', err);
    }

    // Fallback — uses shared DNS config (config/dns.ts)
    const fallback = generateDnsRecords(domain);
    const records: VerificationRecord[] = fallback.map(r => ({
      type: r.type,
      host: r.host,
      value: r.value,
      purpose: r.purpose || r.description || `${r.type} record`,
    }));
    setRecords(records);
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    setError('');
    setSuccess('');

    try {
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      const response = await axios.post(`${API_URL}/signup/verify-domain`, {
        domainId: currentData.domainId,
        token: currentData.token,
      });

      if (response.data?.success && response.data?.data?.verificationStatus) {
        const status = response.data.data.verificationStatus;
        // Map status to per-record index keys
        const newStatus: Record<string, boolean> = {};
        records.forEach((record, index) => {
          if (record.type === 'MX') newStatus[index] = status.mx;
          else if (record.type === 'A') newStatus[index] = status.a || false;
          else if (record.host === '@' && record.value.includes('v=spf1')) newStatus[index] = status.spf;
          else if (record.host.includes('_domainkey')) newStatus[index] = status.dkim;
          else if (record.host === '_dmarc') newStatus[index] = status.dmarc;
        });
        setVerificationStatus(newStatus);

        if (response.data.data.allVerified) {
          setSuccess('All DNS records verified successfully!');
          sessionStorage.setItem('signupData', JSON.stringify({
            ...signupData,
            domainVerified: true,
          }));
        } else {
          const pending: string[] = [];
          if (!status.mx) pending.push('MX');
          if (!status.spf) pending.push('SPF');
          if (!status.dkim) pending.push('DKIM');
          if (!status.dmarc) pending.push('DMARC');
          setError(`Records not yet verified: ${pending.join(', ')}. DNS changes may take up to 48 hours to propagate.`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleContinue = () => {
    navigate('/setup/admin-user');
  };

  const handleSkip = () => {
    sessionStorage.setItem('signupData', JSON.stringify({
      ...signupData,
      skipVerification: true,
    }));
    navigate('/setup/admin-user');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', py: 2, px: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/setup/domain')} sx={{ color: '#64748b' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626', fontFamily: 'Inter, sans-serif' }}>
              CruvzMail
            </Typography>
          </Box>
          <Button component={RouterLink} to="/login" sx={{ color: '#dc2626', fontWeight: 600, textTransform: 'none' }}>
            Sign In
          </Button>
        </Box>
      </Box>

      {/* Header Banner */}
      <Box sx={{ bgcolor: '#f8fafc', py: 3, borderBottom: '1px solid #e5e7eb' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 50, height: 50, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VerifiedIcon sx={{ fontSize: 24, color: '#dc2626' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
              Verify Domain Ownership
            </Typography>
            <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
              Add these DNS records to verify <strong>{domain}</strong>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: 6 }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 4, borderRadius: 2 }}>
            Add the following DNS records to your domain's DNS settings. Changes may take up to 48 hours to propagate.
          </Alert>

          {/* DNS Records Table */}
          <TableContainer sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb', mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Host/Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f1f5f9' } }}>
                    <TableCell>
                      <Chip 
                        label={record.type} 
                        size="small" 
                        sx={{ 
                          bgcolor: record.type === 'MX' ? '#dbeafe' : record.type === 'TXT' ? '#fef3c7' : record.type === 'CNAME' ? '#f3e8ff' : record.type === 'SRV' ? '#e0e7ff' : '#dcfce7', 
                          color: '#374151', 
                          fontWeight: 600, 
                          fontFamily: 'Inter, sans-serif' 
                        }} 
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#374151' }}>
                      {record.host}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151', maxWidth: 300 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {record.value}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#6b7280' }}>
                      {record.purpose}
                    </TableCell>
                    <TableCell>
                      {verificationStatus[index] !== undefined ? (
                        verificationStatus[index] ? (
                          <Chip icon={<CheckIcon />} label="Verified" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534' }} />
                        ) : (
                          <Chip icon={<WarningIcon />} label="Not Found" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} />
                        )
                      ) : (
                        <Chip icon={<WarningIcon />} label="Pending" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={copied === `${index}` ? 'Copied!' : 'Copy value'}>
                        <IconButton 
                          onClick={() => copyToClipboard(record.value, `${index}`)} 
                          size="small" 
                          sx={{ color: copied === `${index}` ? '#22c55e' : '#6b7280' }}
                        >
                          {copied === `${index}` ? <CheckIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Instructions */}
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 4, mb: 4, border: '1px solid #e5e7eb' }}>
            <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
              How to add DNS records:
            </Typography>
            <Box component="ol" sx={{ pl: 2, color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
              <li>Log in to your domain registrar (e.g., GoDaddy, Namecheap, Cloudflare)</li>
              <li>Navigate to DNS settings for <strong>{domain}</strong></li>
              <li>Add each record from the table above</li>
              <li>Save your changes and wait for propagation</li>
              <li>Click "Verify DNS" to check your records</li>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
              sx={{ px: 4, py: 1.5, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#dc2626', bgcolor: 'transparent' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              Skip for Now
            </Button>
            <Button
              variant="outlined"
              onClick={verifyDomain}
              disabled={verifying}
              startIcon={verifying ? <CircularProgress size={18} /> : <RefreshIcon />}
              sx={{ px: 4, py: 1.5, borderColor: '#dc2626', color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              {verifying ? 'Verifying...' : 'Verify DNS'}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinue}
              sx={{ px: 4, py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DomainVerification;
